import React, { useEffect, useState } from 'react';
import './FeatureStyles.css';
import './AIBugCreationFeature.css';

// Initial scattered positions for chaos nodes
const chaosNodes = [
  { id: 1, x: 80, y: 50 },
  { id: 2, x: 240, y: 45 },
  { id: 3, x: 55, y: 130 },
  { id: 4, x: 265, y: 140 },
  { id: 5, x: 90, y: 170 },
  { id: 6, x: 230, y: 165 },
];

const AIBugCreationAnimation = () => {
  const [phase, setPhase] = useState(0);
  // 0: scattered chaos, 1: connecting, 2: converging, 3: complete

  useEffect(() => {
    const interval = setInterval(() => {
      setPhase(prev => (prev + 1) % 4);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const centerX = 160;
  const centerY = 100;

  return (
    <div className="feature-animation abstract ai-bug-creation-abstract">
      <svg className="abstract-canvas" viewBox="0 0 320 200">
        <defs>
          <linearGradient id="creation-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#ec4899" />
          </linearGradient>
        </defs>

        {/* Chaos nodes */}
        {chaosNodes.map((node, i) => {
          // Calculate target position based on phase
          let targetX = node.x;
          let targetY = node.y;
          let opacity = 1;
          let scale = 1;

          if (phase === 1) {
            // Move slightly toward center
            targetX = node.x + (centerX - node.x) * 0.3;
            targetY = node.y + (centerY - node.y) * 0.3;
          } else if (phase === 2) {
            // Move close to center in orbit
            const angle = (i * Math.PI * 2) / chaosNodes.length;
            const radius = 35;
            targetX = centerX + Math.cos(angle) * radius;
            targetY = centerY + Math.sin(angle) * radius;
          } else if (phase === 3) {
            // Merge into center
            targetX = centerX;
            targetY = centerY;
            opacity = 0;
            scale = 0;
          }

          return (
            <g key={node.id}>
              {/* Chaos node */}
              <circle
                className={`chaos-node ${phase === 0 ? 'shake' : ''}`}
                cx={targetX}
                cy={targetY}
                r={7}
                style={{
                  opacity,
                  transform: `scale(${scale})`,
                  transformOrigin: `${targetX}px ${targetY}px`,
                  transitionDelay: `${i * 0.1}s`
                }}
              />
            </g>
          );
        })}

        {/* Central organizing node (phase 1+) */}
        {phase >= 1 && (
          <g className="central-node-group">
            {/* Central node */}
            <circle
              className={`abstract-node ${phase === 3 ? 'blue active' : 'pink'} ${phase === 1 || phase === 2 ? 'active' : ''} central-node`}
              cx={centerX}
              cy={centerY}
              r={phase === 3 ? 32 : 20}
            />

            {/* Checkmark icon (phase 3) */}
            {phase === 3 && (
              <path
                className="central-check"
                d="M 148 100 L 156 108 L 172 92"
                fill="none"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
          </g>
        )}
      </svg>

      {/* Result label */}
      {phase === 3 && <span className="result-label">Detected</span>}
    </div>
  );
};

const AIBugCreationFeature = {
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  title: "AI Bug Detection",
  description: "When tests fail due to real bugs, AI automatically analyzes the failure, captures DOM state, identifies the root cause, and creates detailed bug tickets in Jira - complete with reproduction steps. No manual triage needed.",
  animation: <AIBugCreationAnimation />
};

export default AIBugCreationFeature;
